import React, { useEffect, useRef, useState } from "react";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import {
  CustomButton,
  CustomDialog,
  CustomIconButton,
} from "../../../../../../../../Components";
import { Box } from "@material-ui/core";
import { getDownloadableLink } from "../../../../../../../../Helper";
import { VideoPublishDetailsComponent } from "../VideoPublishDetailsComponent";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";
import {
  CreateOrUpdatePropertyVideoInfo,
  CreateOrUpdateUnitVideoInfo,
} from "../../../../../../../../Services";

// styles
import useStyles from "./styles";

// Icons
import {
  Download01,
  PauseIcon,
  PlayButtonIcon,
} from "../../../../../../../../assets/icons";

const initialVideoPublishData = {
  propertyId: null,
  categoryId: null,
  fileId: null,
  videoTitle: "",
  videoDescription: "",
  isPublished: false,
  isMarketing: false,
  isNeedOwnerApproval: false,
  isApproved: false,
  approveOwnerId: null,
  approvedById: null,
  approvalDate: null,
  comments: "",
  unitId: null,
  categoryName: "",
};

function PropertyVideoPreviewDialog({ onOpen, onClose, data, activeVideo }) {
  const styles = useStyles();
  const { translate } = useTranslate("NewPropertiesView");
  const { setAlertBoxContent } = useVerticalNav();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const videoRef = useRef(null);
  const thumbnailsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoPublishData, setVideoPublishData] = useState(
    initialVideoPublishData
  );
  const validVideos = data?.videos?.filter((v) => v.fileId);
  const isUnit = !!selectedVideo?.unitId;

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleScroll = (direction) => {
    const container = thumbnailsRef.current;
    if (!container) return;

    const scrollAmount = 120 * 3;
    container.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  const handleSelectVideo = (video, index) => {
    setSelectedVideo(video);
    setSelectedVideoIndex(index);
    setIsPlaying(false);
  };

  const handleSavePublishVideoInfo = async () => {
    try {
      isUnit
        ? await CreateOrUpdateUnitVideoInfo(videoPublishData)
        : await CreateOrUpdatePropertyVideoInfo(videoPublishData);
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("Changes-saved-successfully"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      onClose();
    } catch {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("unable-to-save-changes-Please-try-again"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
  };

  const downloadDocument = (item) => {
    if (!item) {
      return;
    }
    try {
      const link = document.createElement("a");
      link.setAttribute("download", item.fileName);
      link.href = getDownloadableLink(item.fileId);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  useEffect(() => {
    if (validVideos?.length > 0) {
      const initialIndex = activeVideo
        ? validVideos.findIndex((v) => v.fileId === activeVideo.fileId)
        : 0;

      setSelectedVideo(validVideos[initialIndex >= 0 ? initialIndex : 0]);
      setSelectedVideoIndex(initialIndex >= 0 ? initialIndex : 0);
    }
  }, [data, activeVideo]);

  useEffect(() => {
    if (selectedVideo?.fileId) {
      const url = getDownloadableLink(selectedVideo.fileId);
      setVideoUrl(url);
    } else {
      setVideoUrl(null);
    }
  }, [selectedVideo]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoUrl]);

  return (
    <CustomDialog
      open={onOpen}
      onClose={() => {
        onClose();
      }}
      title={translate("Preview-videos")}
      subtitle={translate("View-and-edit-attach-files")}
      cancelText={translate("Discard")}
      confirmText={translate("Save-changes")}
      onConfirm={() => {
        handleSavePublishVideoInfo();
      }}
      contentDialogClass={styles.containerContentDialog}
      width={"1024px"}
      content={
        <Box className={styles.wrapperPreview}>
          <Box className={styles.containerItems}>
            {" "}
            <Box className={styles.containerMainItem}>
              <video
                ref={videoRef}
                className={styles.videoPreview}
                height="100%"
                width="100%"
              >
                <source src={videoUrl} type="video/mp4" />
                <source src={videoUrl} type="video/webm" />
                <source src={videoUrl} type="video/ogg" />
              </video>

              <Box
                className={styles.conatinerPlayPauseIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
                sx={{ cursor: "pointer" }}
              >
                {isPlaying ? (
                  <PauseIcon
                    height="80"
                    width="80"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <PlayButtonIcon
                    height="80"
                    width="80"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </Box>
            </Box>
            <Box className={styles.containerSubItemsSection}>
              <Box className={styles.containerNextPrevButton}>
                <CustomIconButton
                  variant="text"
                  size="xs"
                  boxShadow="none"
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleScroll("left");
                  }}
                >
                  <ChevronLeft
                    width="20"
                    height="20"
                    fill={palette.foreground.quarterary}
                  />
                </CustomIconButton>
              </Box>

              <Box
                className={styles.videoThumbnailsContainer}
                ref={thumbnailsRef}
              >
                {data?.videos?.map((vid, index) => (
                  <Box
                    key={vid.fileId}
                    className={styles.videoThumbnailWrapper}
                    onClick={() => handleSelectVideo(vid, index)}
                    sx={{
                      border:
                        index === selectedVideoIndex
                          ? `2px solid ${palette.button.primary_bg}`
                          : "2px solid transparent",
                      borderRadius: "6px",
                      overflow: "hidden",
                      cursor: "pointer",
                      flex: "0 0 auto",
                    }}
                  >
                    <video
                      src={getDownloadableLink(vid.fileId)}
                      width="100%"
                      height="100%"
                      muted
                      style={{ objectFit: "cover" }}
                    />

                    <Box
                      className={styles.conatinerPlayPauseIcon}
                      sx={{ cursor: "pointer" }}
                    >
                      <PlayButtonIcon
                        height="48"
                        width="48"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box className={styles.containerNextPrevButton}>
                <CustomIconButton
                  variant="text"
                  size="xs"
                  boxShadow="none"
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleScroll("right");
                  }}
                >
                  <ChevronRight
                    width="20"
                    height="20"
                    fill={palette.foreground.quarterary}
                  />
                </CustomIconButton>
              </Box>
            </Box>
          </Box>
          <VideoPublishDetailsComponent
            activeVideo={selectedVideo}
            videoPublishData={videoPublishData}
            setVideoPublishData={setVideoPublishData}
          />
        </Box>
      }
      moreAction={
        <CustomButton
          boxShadow="none"
          size="lg"
          variant="text"
          color="tertiaryGray"
          startIcon={
            <Download01
              width="20"
              height="20"
              fill={palette.button.tertiaryGray_fg}
            />
          }
          onClick={() => {
            downloadDocument(selectedVideo);
          }}
          style={{ marginInlineEnd: "auto" }}
        >
          {translate("Download")}
        </CustomButton>
      }
    ></CustomDialog>
  );
}

export default PropertyVideoPreviewDialog;
